/**
 * PracticeTourNudge — Always-visible practice tour access for doctors.
 *
 * Dual mode:
 *   1. FIRST TIME (rx_tour_completed=false) — Prominent banner with dismiss
 *   2. COMPLETED (rx_tour_completed=true)  — Compact sticky pill: "📝 Practice"
 *
 * Always shows for DOCTOR role. First-time banner is dismissible per
 * session; once dismissed it collapses to the compact pill.
 *
 * Props:
 *   isDark — dark mode flag
 */
import React, { useState, useEffect, useCallback } from 'react';
import { specialisationName } from '../../constants/staff';
import { useNavigate } from 'react-router-dom';
import { fetchTourStatus } from '../../services/api/prescriptionTourApi';
import { ROUTES } from '../../constants/routes';

const SESSION_KEY = 'rx_tour_nudge_dismissed';

export default function PracticeTourNudge({ isDark = false }) {
  const navigate = useNavigate();
  const [isDoctor, setIsDoctor] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => !!sessionStorage.getItem(SESSION_KEY)
  );
  const [specialization, setSpecialization] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchTourStatus().then((status) => {
      if (cancelled) return;
      if (status && status.role === 'DOCTOR') {
        setIsDoctor(true);
        setTourCompleted(!!status.rx_tour_completed);
        setSpecialization(status.specialization);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setBannerDismissed(true);
  }, []);

  const handleStart = useCallback(() => {
    navigate(ROUTES.PRACTICE_PRESCRIPTION);
  }, [navigate]);

  // Not a doctor → render nothing
  if (!isDoctor) return null;

  // ── Compact pill: shown after tour completed OR after banner dismissed ──
  if (tourCompleted || bannerDismissed) {
    return (
      <button
        onClick={handleStart}
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.3125rem',
          padding: '0.3125rem 0.625rem',
          borderRadius: '2rem',
          border: `1px solid ${isDark ? 'rgba(20,184,166,0.18)' : 'rgba(20,184,166,0.12)'}`,
          background: isDark
            ? 'rgba(20,184,166,0.06)'
            : 'rgba(20,184,166,0.04)',
          fontSize: '0.6875rem',
          fontWeight: 500,
          color: isDark ? '#5eead4' : '#0d9488',
          fontFamily: 'system-ui, sans-serif',
          transition: 'background 0.15s, border-color 0.15s',
          whiteSpace: 'nowrap',
        }}
        title="Open prescription practice mode"
      >
        <span style={{ fontSize: '0.75rem' }}>📝</span>
        Practice Rx
      </button>
    );
  }

  // ── Full banner: first-time doctors who haven't dismissed ──────────
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.625rem 0.875rem',
      borderRadius: '0.75rem',
      border: `1px solid ${isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.15)'}`,
      background: isDark
        ? 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(13,148,136,0.04))'
        : 'linear-gradient(135deg, rgba(20,184,166,0.06), rgba(13,148,136,0.03))',
      fontFamily: 'system-ui, sans-serif',
      animation: 'fadeIn 0.3s ease-out',
    }}>
      <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>📝</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: isDark ? '#5eead4' : '#0d9488',
        }}>
          Try Digital Prescriptions
        </p>
        <p style={{
          margin: '0.125rem 0 0',
          fontSize: '0.6875rem',
          color: isDark ? '#9ca3af' : '#6b7280',
        }}>
          2-min guided practice with sample patients
          {/* FE-35 — the speciality's NAME; the title-caser is only the
              fallback for a value this build has no label for. */}
          {specialization && ` for ${specialisationName(specialization)
            || specialization.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}`}
        </p>
      </div>
      <button
        onClick={handleStart}
        style={{
          cursor: 'pointer',
          padding: '0.375rem 0.75rem',
          borderRadius: '0.375rem',
          border: 'none',
          background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
          color: '#fff',
          fontSize: '0.6875rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          transition: 'opacity 0.15s',
        }}
      >
        Start Practice →
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1.25rem',
          height: '1.25rem',
          borderRadius: '50%',
          border: 'none',
          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          color: isDark ? '#6b7280' : '#9ca3af',
          fontSize: '0.625rem',
          flexShrink: 0,
          transition: 'background 0.15s',
        }}
      >
        ✕
      </button>
    </div>
  );
}
